import CloseIcon from '@mui/icons-material/Close';
import { Button, IconButton, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import ModalV2 from '/@/components/bayer/modal-v2';
import Spinner from '/@/components/spinner';
import { useCatalogProductById } from '/@/hooks/catalog';
import { _ } from '/@/locale';
import { useCreateYieldEstimationMutation } from '/@/store/crop-monitoring/apiProviders';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import { getCropMonitoringData } from '/@/utils/crop-monitoring';
import YieldEstimationGuide from '../yield-estimation-guide';
import YieldEstimationFormset from './components/formset';
import YieldEstimationResult from './components/result';

export interface YieldSampleAPI {
    spikesPerHectare: number;
    grainsPerSpike: number;
    thousandGrainsWeight: number;
    result?: number;
    name: string;
}
export interface YieldEstimationAPI {
    cropMonitoring: number;
    status: 'draft' | 'saved' | 'archived';
    result?: number;
    yieldSamples: YieldSampleAPI[];
}

export type YieldEstimationForm = {
    samples: {
        name: string;
        spikes: number | '';
        grainsSpike: number | '';
        weight1000Grains: number | '';
    }[];
};

const YieldEstimationForm = ({
    cropMonitoring,
    setYieldEstimationDrawerIsOpen,
    setCloseModalIsOpen,
}: {
    cropMonitoring: CropMonitoring;
    setYieldEstimationDrawerIsOpen: (bool: boolean) => void;
    setCloseModalIsOpen: (bool: boolean) => void;
}) => {
    const [guideModalIsOpen, setGuideModalIsOpen] = useState(false);
    const [step, setStep] = useState<'form' | 'result'>('form');

    const createYieldEstimationMutation = useCreateYieldEstimationMutation({ cropMonitoringId: cropMonitoring.id });
    const cropData = getCropMonitoringData(cropMonitoring);
    const yieldEstimationFromCM = cropData?.yieldEstimation as YieldEstimationAPI | undefined;
    const yieldEstimation =
        createYieldEstimationMutation.data || // Try get first from mutation (if recently edited)
        yieldEstimationFromCM; // If not, get from crop monitoring (previously created)
    console.log('cropMonitoring', cropMonitoring);
    const { data: catalogProduct, isLoading: loadingCatalogProduct } = useCatalogProductById({
        id: cropMonitoring?.cornCropMonitoring?.product,
        crop: 'corn',
    });

    const weight1000GrainsFeature = catalogProduct?.features?.find((feature: { name: string; value: string }) => {
        return _(feature.name).includes('1000');
    });

    const weight1000GrainsDefaultValue = weight1000GrainsFeature
        ? Number.parseInt(_(weight1000GrainsFeature.value))
        : undefined;

    const defaultValue = [
        { name: '', spikes: '', grainsSpike: '', weight1000Grains: '' },
    ] as YieldEstimationForm['samples'];

    const yieldEstimationParsedSamples = (yieldEstimation?.yieldSamples || []).map((sample) => ({
        name: sample.name,
        spikes: Number(sample.spikesPerHectare),
        grainsSpike: Number(sample.grainsPerSpike),
        weight1000Grains: Number(sample.thousandGrainsWeight) * 1000, // KG => g
    }));

    const form = useForm<YieldEstimationForm>({
        defaultValues: {
            samples: yieldEstimationParsedSamples.length > 0 ? yieldEstimationParsedSamples : defaultValue,
        },
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: <form.reset is not a dependency by official docs https://react-hook-form.com/docs/useform/reset>
    useEffect(() => {
        if (weight1000GrainsDefaultValue && !yieldEstimation) {
            form.reset({
                samples: [{ name: '', spikes: '', grainsSpike: '', weight1000Grains: weight1000GrainsDefaultValue }],
            });
        }
    }, [weight1000GrainsDefaultValue]);

    const renderFormContent = () => {
        if (step === 'form') {
            if (loadingCatalogProduct) {
                return (
                    <Stack alignItems='center' justifyContent='center' height='100%'>
                        <Spinner />
                    </Stack>
                );
            }
            return (
                <YieldEstimationFormset
                    cropMonitoringId={cropMonitoring.id}
                    form={form}
                    setGuideModalIsOpen={setGuideModalIsOpen}
                    setCloseModalIsOpen={setCloseModalIsOpen}
                    goNext={() => setStep('result')}
                    weight1000GrainsDefaultValue={weight1000GrainsDefaultValue}
                />
            );
        }
        return null;
    };

    return (
        <Stack width={{ xs: 'auto', sm: '500px' }} height='100%' overflow={'hidden'}>
            <Stack
                direction={'row'}
                borderBottom={(t) => `1px solid ${t.palette.divider}`}
                p={3}
                justifyContent={'space-between'}
                alignItems={'center'}
            >
                <Typography color={'primary'} variant='subtitle1'>
                    {_('Estimación de rendimiento del cultivo')}
                </Typography>
                <IconButton onClick={() => setCloseModalIsOpen(true)}>
                    <CloseIcon fontSize='large' />
                </IconButton>
            </Stack>
            {renderFormContent()}
            {step === 'result' && yieldEstimation && (
                <YieldEstimationResult
                    cropMonitoringId={cropMonitoring.id}
                    yieldEstimation={yieldEstimation}
                    goBack={() => setStep('form')}
                    setYieldEstimationDrawerIsOpen={setYieldEstimationDrawerIsOpen}
                />
            )}

            <ModalV2
                open={guideModalIsOpen}
                onClose={() => {
                    setGuideModalIsOpen(false);
                }}
                actions={
                    <Button
                        color='secondary'
                        onClick={() => {
                            setGuideModalIsOpen(false);
                        }}
                    >
                        {_('Entendido')}
                    </Button>
                }
                title={_('Guía rápida para tomar muestras')}
                actionsProps={{ sx: { justifyContent: 'end' } }}
                maxWidth={'md'}
            >
                <YieldEstimationGuide />
            </ModalV2>
        </Stack>
    );
};

export default YieldEstimationForm;
